import React from 'react';
import { DocumentDuplicateIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { ConversationNodeClass, TransitionConditionType } from '../../types/workflow-spec';
import { WorkflowConnection, WorkflowNode } from '../../types/workflow';
import type { WorkflowAgentInstance } from '../../services/workflowService';
import { WorkflowTriggerHubSpotPanel } from './WorkflowTriggerHubSpotPanel';
import { Button } from '../ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../ui/Field';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
const WEBHOOK_BASE = process.env.REACT_APP_CORE_MS_WEBHOOK_BASE_URL || API_BASE;

interface WorkflowInspectorProps {
  workflowId?: string;
  selectedNode?: WorkflowNode;
  selectedConnection?: WorkflowConnection;
  entryAgents: WorkflowAgentInstance[];
  functionalAgents: WorkflowAgentInstance[];
  onUpdateNode: (nodeId: string, updates: Partial<WorkflowNode>) => void;
  onDeleteNode: (nodeId: string) => void;
  onUpdateConnection: (connectionId: string, updates: Partial<WorkflowConnection>) => void;
  onDeleteConnection: (connectionId: string) => void;
  onAddRouterBranch: (nodeId: string) => void;
  /** Clears the selection — closes the drawer. */
  onClose?: () => void;
  /** Persists the whole workflow as a draft (same as the toolbar "Save draft"). */
  onSaveDraft?: () => void;
}

const NODE_CLASS_OPTIONS: ConversationNodeClass[] = [
  'trigger',
  'conversation',
  'action',
  'router',
  'human-handoff',
  'end',
];

const CONDITION_TYPE_OPTIONS: TransitionConditionType[] = [
  'intent',
  'outcome',
  'user-response',
  'fallback',
  'custom',
];

const TRIGGER_TYPE_TO_ENTRY: Record<string, string> = {
  manual: 'text',
  webhook: 'text',
  'inbound-call': 'inbound-voice',
  'outbound-call': 'outbound-voice',
  whatsapp: 'whatsapp',
};


const copyText = (text: string, label: string) => {
  void navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
};

const normalizeWebhookUrl = (url: string) => url.replace("/core/core", "/core");

const renderWebhookUrl = (label: string, url: string) => (
  <div>
    <p className="mb-1 text-xs font-medium text-gray-600">{label}</p>
    <div className="flex items-center gap-2">
      <code className="flex-1 break-all rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-[11px] text-blue-700">
        POST {normalizeWebhookUrl(url)}
      </code>
      <Button tone="custom"
        type="button"
        onClick={() => copyText(normalizeWebhookUrl(url), label)}
        className="flex-shrink-0 rounded border border-gray-200 p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
      >
        <DocumentDuplicateIcon className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

const ENTRY_TYPE_LABELS: Record<string, string> = {
  text: 'Text (Chat)',
  'inbound-voice': 'Inbound Voice',
  'outbound-voice': 'Outbound Voice',
};

const filterEntryAgents = (agents: WorkflowAgentInstance[], triggerType?: string) => {
  const requiredEntryType = triggerType ? TRIGGER_TYPE_TO_ENTRY[triggerType] : undefined;
  if (!requiredEntryType) return agents;
  return agents.filter((agent) => agent.entryType === requiredEntryType);
};

const resolveNodeAgentId = (node: WorkflowNode) => String(node.data?.agentId ?? '').trim();

const agentsForNodeSelect = (
  node: WorkflowNode,
  agents: WorkflowAgentInstance[]
): WorkflowAgentInstance[] => {
  const agentId = resolveNodeAgentId(node);
  if (!agentId || agents.some((agent) => agent.id === agentId)) {
    return agents;
  }
  return [
    {
      id: agentId,
      name: node.title || node.name || 'Assigned agent',
      code: 'assigned',
      agentRole: 'functional',
      version: String(node.data?.agentVersion ?? '1.0.0'),
      isActive: true,
    },
    ...agents,
  ];
};

const renderAgentFields = (
  node: WorkflowNode,
  agents: WorkflowAgentInstance[],
  label: string,
  onUpdateNode: WorkflowInspectorProps['onUpdateNode'],
  updateNodeData: (node: WorkflowNode, updates: Record<string, unknown>) => void
) => {
  const selectableAgents = agentsForNodeSelect(node, agents);
  const selectedAgentId = resolveNodeAgentId(node);

  return (
  <>
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>
      <SelectField tone="default"
        className="w-full rounded-md border px-3 py-2 text-sm"
        value={selectedAgentId}
        onChange={(e) => {
          const selected = selectableAgents.find((agent) => agent.id === e.target.value);
          onUpdateNode(node.id, {
            data: {
              ...node.data,
              agentId: e.target.value,
              agentVersion: selected?.version ?? '1',
            },
            ...(selected?.name && label === 'Functional Agent'
              ? { title: selected.name, name: selected.name }
              : {}),
          });
        }}
      >
        <option value="">Select an agent</option>
        {selectableAgents.map((agent) => (
          <option key={agent.id} value={agent.id}>
            {agent.name}
            {agent.entryType ? ` (${ENTRY_TYPE_LABELS[agent.entryType] ?? agent.entryType})` : ''}
          </option>
        ))}
      </SelectField>
    </label>

    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-600">Agent Version</span>
      <TextField tone="default"
        className="w-full rounded-md border px-3 py-2 text-sm"
        value={String(node.data?.agentVersion ?? '')}
        onChange={(e) => updateNodeData(node, { agentVersion: e.target.value })}
      />
    </label>
  </>
  );
};

export const WorkflowInspector: React.FC<WorkflowInspectorProps> = ({
  workflowId,
  selectedNode,
  selectedConnection,
  entryAgents,
  functionalAgents,
  onUpdateNode,
  onDeleteNode,
  onUpdateConnection,
  onDeleteConnection,
  onAddRouterBranch,
  onClose,
  onSaveDraft,
}) => {
  const updateNodeData = (node: WorkflowNode, updates: Record<string, unknown>) => {
    onUpdateNode(node.id, { data: { ...node.data, ...updates } });
  };

  const renderNodeEditor = (node: WorkflowNode) => {
    const nodeClass = (node.nodeClass ?? node.data?.nodeClass ?? 'action') as ConversationNodeClass;
    const triggerType = String(node.data?.triggerType ?? 'manual');
    const effectiveEntryType = triggerType === 'webhook' ? 'outbound-voice' : TRIGGER_TYPE_TO_ENTRY[triggerType];
    const availableEntryAgents = entryAgents.filter(
      (a) => !effectiveEntryType || a.entryType === effectiveEntryType
    );

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Node Settings</h3>
          <p className="mt-1 text-xs text-gray-500">Edit the selected workflow step.</p>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Title</span>
          <TextField tone="default"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={node.title ?? node.name}
            onChange={(e) => onUpdateNode(node.id, { title: e.target.value, name: e.target.value })}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Summary</span>
          <TextArea tone="default"
            className="min-h-24 w-full rounded-md border px-3 py-2 text-sm"
            value={node.summary ?? String(node.data?.description ?? '')}
            onChange={(e) => onUpdateNode(node.id, { summary: e.target.value, data: { ...node.data, description: e.target.value } })}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Node Class</span>
          <SelectField tone="default"
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={nodeClass}
            onChange={(e) => onUpdateNode(node.id, { nodeClass: e.target.value as ConversationNodeClass, data: { ...node.data, nodeClass: e.target.value } })}
          >
            {NODE_CLASS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </SelectField>
        </label>

        {nodeClass === 'router' ? (
          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">Branches</span>
              <Button tone="link"
                className="text-xs font-medium"
                onClick={() => onAddRouterBranch(node.id)}
              >
                Add branch
              </Button>
            </div>
            {node.outputs.map((output) => (
              <div key={output.id} className="flex items-center gap-2">
                <TextField tone="default"
                  className="flex-1 rounded-md border px-3 py-2 text-sm"
                  value={output.name}
                  onChange={(e) =>
                    onUpdateNode(node.id, {
                      outputs: node.outputs.map((candidate) =>
                        candidate.id === output.id ? { ...candidate, name: e.target.value } : candidate
                      ),
                    })
                  }
                />
                {node.outputs.length > 2 ? (
                  <Button tone="danger-soft"
                    className="rounded-md border px-2 py-2 text-xs font-medium"
                    onClick={() =>
                      onUpdateNode(node.id, {
                        outputs: node.outputs.filter((candidate) => candidate.id !== output.id),
                      })
                    }
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {(nodeClass === 'conversation' || nodeClass === 'action') && (
          <>
            {renderAgentFields(
              node,
              functionalAgents,
              nodeClass === 'action' ? 'Functional Agent' : 'Agent',
              onUpdateNode,
              updateNodeData
            )}
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Instruction</span>
              <TextArea tone="default"
                className="min-h-24 w-full rounded-md border px-3 py-2 text-sm"
                value={String(node.data?.instruction ?? '')}
                onChange={(e) => updateNodeData(node, { instruction: e.target.value })}
              />
            </label>
          </>
        )}

        {nodeClass === 'trigger' ? (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs font-medium text-gray-600">Trigger Configuration</div>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Trigger Type</span>
              <SelectField tone="default"
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={triggerType}
                onChange={(e) => {
                  const nextTriggerType = e.target.value;
                  const nextEntryType = nextTriggerType === 'webhook' ? 'outbound-voice' : TRIGGER_TYPE_TO_ENTRY[nextTriggerType];
                  const nextAgents = entryAgents.filter((a) => !nextEntryType || a.entryType === nextEntryType);
                  const currentAgentId = String(node.data?.agentId ?? '');
                  const stillValid = nextAgents.some((agent) => agent.id === currentAgentId);
                  const autoSecret =
                    nextTriggerType === 'webhook' && !node.data?.webhookSecret
                      ? Array.from(crypto.getRandomValues(new Uint8Array(32)))
                          .map((b) => b.toString(16).padStart(2, '0'))
                          .join('')
                      : undefined;
                  const autoVerifyToken =
                    nextTriggerType === 'whatsapp' && !node.data?.whatsappVerifyToken
                      ? Array.from(crypto.getRandomValues(new Uint8Array(24)))
                          .map((b) => b.toString(16).padStart(2, '0'))
                          .join('')
                      : undefined;
                  updateNodeData(node, {
                    triggerType: nextTriggerType,
                    ...(autoSecret ? { webhookSecret: autoSecret } : {}),
                    ...(autoVerifyToken ? { whatsappVerifyToken: autoVerifyToken } : {}),
                    ...(stillValid ? {} : { agentId: '', agentVersion: '' }),
                  });
                }}
              >
                <option value="manual">manual</option>
                <option value="inbound-call">inbound-call</option>
                <option value="outbound-call">outbound-call</option>
                <option value="webhook">webhook</option>
                <option value="whatsapp">whatsapp</option>
              </SelectField>
            </label>
            {renderAgentFields(node, availableEntryAgents, 'Entry Agent', onUpdateNode, updateNodeData)}
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Entry Greeting</span>
              <TextArea tone="default"
                className="min-h-20 w-full rounded-md border px-3 py-2 text-sm"
                value={String(node.data?.entryGreeting ?? '')}
                onChange={(e) => updateNodeData(node, { entryGreeting: e.target.value })}
              />
            </label>
            {node.data?.triggerType === 'webhook' && workflowId ? (
              <div className="space-y-3 rounded-md border border-blue-100 bg-blue-50/50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Webhook Trigger</p>
                <p className="text-xs text-gray-500">
                  Point your external system at the URL below. Sign each request with HMAC-SHA256 using the secret and
                  send the signature in <code className="rounded bg-white px-1">X-Webhook-Signature</code>.
                </p>
                {renderWebhookUrl('Webhook URL', `${WEBHOOK_BASE}/core/webhooks/workflow/${workflowId}`)}
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-600">Signing Secret</p>
                  <div className="flex items-center gap-2">
                    {node.data?.webhookSecret ? (
                      <code className="flex-1 break-all rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-[11px] text-gray-800">
                        {String(node.data.webhookSecret)}
                      </code>
                    ) : (
                      <span className="flex-1 text-xs text-gray-400">No secret — switch trigger type away and back to generate one</span>
                    )}
                    <Button tone="custom"
                      type="button"
                      title="Copy secret"
                      onClick={() => copyText(String(node.data?.webhookSecret ?? ''), 'Signing secret')}
                      className="flex-shrink-0 rounded border border-gray-200 p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <ol className="list-decimal space-y-1 pl-4 text-xs text-gray-500">
                  <li>Copy the signing secret above and save the workflow.</li>
                  <li>Configure your external system to POST to the webhook URL with the signed payload.</li>
                </ol>
              </div>
            ) : null}
            {node.data?.triggerType === 'inbound-call' || node.data?.triggerType === 'outbound-call' ? (
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Phone Number / DID</span>
                <TextField tone="default"
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  value={String(node.data?.phoneNumber ?? '')}
                  onChange={(e) => updateNodeData(node, { phoneNumber: e.target.value })}
                  placeholder="+14155551234"
                />
                <p className="mt-1 text-xs text-gray-400">E.164 format. Used as the Twilio caller ID for outbound calls.</p>
              </label>
            ) : null}
            {workflowId && node.data?.triggerType === 'inbound-call' ? (
              <div className="space-y-2 rounded-md border border-blue-100 bg-blue-50/50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Twilio Webhooks</p>
                <p className="text-xs text-gray-500">
                  Set these on your Twilio phone number under Voice Configuration. Save the workflow first so the ID is stable.
                </p>
                {renderWebhookUrl('Inbound Call Webhook', `${WEBHOOK_BASE}/core/workflows/voice/inbound/${workflowId}`)}
                {renderWebhookUrl('Status Callback', `${WEBHOOK_BASE}/core/workflows/voice/status/${workflowId}`)}
              </div>
            ) : null}
            {workflowId && node.data?.triggerType === 'outbound-call' ? (
              <WorkflowTriggerHubSpotPanel
                entryAgentId={String(node.data?.agentId ?? '')}
                workflowId={workflowId}
              />
            ) : null}
            {node.data?.triggerType === 'whatsapp' ? (
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Sender Number</span>
                <TextField tone="default"
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  value={String(node.data?.whatsappSenderNumber ?? '')}
                  onChange={(e) => updateNodeData(node, { whatsappSenderNumber: e.target.value })}
                  placeholder="919311347156"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Your registered Karix WABA number in E.164 format without the + (e.g. 919311347156).
                  Configure the API Key in the entry agent&apos;s WhatsApp Config tab.
                </p>
              </label>
            ) : null}
            {workflowId && node.data?.triggerType === 'whatsapp' ? (
              <div className="space-y-3 rounded-md border border-blue-100 bg-blue-50/50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Karix Webhook</p>
                <p className="text-xs text-gray-500">
                  Register the URL below in Karix dashboard → WhatsApp Campaign → Webhook Configuration for your number.
                  Add a custom HTTP header <code className="rounded bg-white px-1">x-karix-token</code> with the verify
                  token below so inbound requests can be authenticated.
                </p>
                {renderWebhookUrl('Webhook URL', `${WEBHOOK_BASE}/core/workflows/whatsapp/inbound/${workflowId}`)}
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-600">Verify Token</p>
                  <div className="flex items-center gap-2">
                    {node.data?.whatsappVerifyToken ? (
                      <code className="flex-1 break-all rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-[11px] text-gray-800">
                        {String(node.data.whatsappVerifyToken)}
                      </code>
                    ) : (
                      <span className="flex-1 text-xs text-gray-400">No token — switch trigger type away and back to generate one</span>
                    )}
                    <Button tone="custom"
                      type="button"
                      title="Copy verify token"
                      onClick={() => navigator.clipboard.writeText(String(node.data?.whatsappVerifyToken ?? ''))}
                      className="flex-shrink-0 rounded border border-gray-200 p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {nodeClass === 'human-handoff' ? (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs font-medium text-gray-600">Handoff Configuration</div>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Transfer Number / SIP</span>
              <TextField tone="default"
                className="w-full rounded-md border px-3 py-2 text-sm"
                placeholder="+15551234567 or sip:agent@domain"
                value={String(node.data?.transferNumber ?? '')}
                onChange={(e) => updateNodeData(node, { transferNumber: e.target.value })}
              />
              <span className="mt-1 block text-[11px] text-gray-400">
                Warm-transfer the live call here. Leave blank to fall back to the global default or a callback message.
              </span>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Handoff Message</span>
              <TextArea tone="default"
                className="min-h-20 w-full rounded-md border px-3 py-2 text-sm"
                value={String(node.data?.handoffMessage ?? '')}
                onChange={(e) => updateNodeData(node, { handoffMessage: e.target.value })}
              />
            </label>
          </div>
        ) : null}

        {nodeClass === 'end' ? (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs font-medium text-gray-600">Completion Configuration</div>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Result</span>
              <SelectField tone="default"
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={String(node.data?.result ?? 'success')}
                onChange={(e) => updateNodeData(node, { result: e.target.value })}
              >
                <option value="success">success</option>
                <option value="failure">failure</option>
              </SelectField>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Closing Message</span>
              <TextArea tone="default"
                className="min-h-20 w-full rounded-md border px-3 py-2 text-sm"
                value={String(node.data?.closingMessage ?? '')}
                onChange={(e) => updateNodeData(node, { closingMessage: e.target.value })}
              />
            </label>
          </div>
        ) : null}

        {nodeClass === 'router' ? (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">Routing Expression</span>
            <TextArea tone="default"
              className="min-h-20 w-full rounded-md border px-3 py-2 font-mono text-xs"
              value={String(node.data?.expression ?? '')}
              onChange={(e) => updateNodeData(node, { expression: e.target.value })}
            />
          </label>
        ) : null}

        <Button tone="danger-soft"
          className="w-full rounded-md border px-3 py-2 text-sm font-medium"
          onClick={() => onDeleteNode(node.id)}
        >
          Delete Node
        </Button>
      </div>
    );
  };

  const renderConnectionEditor = (connection: WorkflowConnection) => (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Transition Settings</h3>
        <p className="mt-1 text-xs text-gray-500">Label and route the selected transition.</p>
      </div>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-600">Label</span>
        <TextField tone="default"
          className="w-full rounded-md border px-3 py-2 text-sm"
          value={connection.label ?? ''}
          onChange={(e) => onUpdateConnection(connection.id, { label: e.target.value })}
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-600">Condition Type</span>
        <SelectField tone="default"
          className="w-full rounded-md border px-3 py-2 text-sm"
          value={connection.conditionType ?? 'custom'}
          onChange={(e) => onUpdateConnection(connection.id, { conditionType: e.target.value as TransitionConditionType })}
        >
          {CONDITION_TYPE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectField>
      </label>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-600">Priority</span>
        <TextField tone="default"
          className="w-full rounded-md border px-3 py-2 text-sm"
          type="number"
          value={connection.priority ?? 0}
          onChange={(e) => onUpdateConnection(connection.id, { priority: Number(e.target.value) })}
        />
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <Checkbox
         
          checked={Boolean(connection.isLoopback)}
          onChange={(e) => onUpdateConnection(connection.id, { isLoopback: e.target.checked })}
        />
        Mark as loopback transition
      </label>

      <Button tone="danger-soft"
        className="w-full rounded-md border px-3 py-2 text-sm font-medium"
        onClick={() => onDeleteConnection(connection.id)}
      >
        Delete Transition
      </Button>
    </div>
  );

  // Drawer: only visible when a node or transition is selected. Slides in from
  // the right over the canvas with a dimmed backdrop; ✕ / Esc / backdrop closes it.
  const isOpen = !!(selectedNode || selectedConnection);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Dimmed backdrop — click to close. */}
      <div
        aria-hidden
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      {/* Slide-in drawer. */}
      <aside
        role="dialog"
        aria-label="Workflow inspector"
        aria-hidden={!isOpen}
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col border-l border-gray-200 bg-white shadow-2xl transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">
            {selectedNode ? 'Node Settings' : selectedConnection ? 'Transition' : 'Inspector'}
          </h3>
          <Button tone="icon-ghost"
            type="button"
            onClick={onClose}
            aria-label="Close inspector"
            className="rounded-md p-1.5 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {selectedNode ? renderNodeEditor(selectedNode) : null}
          {!selectedNode && selectedConnection ? renderConnectionEditor(selectedConnection) : null}
        </div>
        {onSaveDraft ? (
          <div className="border-t border-gray-100 p-4">
            <Button tone="blue"
              type="button"
              onClick={onSaveDraft}
              className="w-full rounded-md px-4 py-2 text-sm font-medium transition-colors"
            >
              Save
            </Button>
            <p className="mt-2 text-center text-xs text-gray-400">Saves the workflow as a draft. Publish to make changes live.</p>
          </div>
        ) : null}
      </aside>
    </>
  );
};

export default WorkflowInspector;
